import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import startOfDay from 'date-fns/startOfDay'

import { parseCalendarDate } from 'utils/dateUtils'

import { toCalendarIso } from '../collectionPeriodBounds'

import { formatCampaignInfoDate } from './campaignInfoAdapter'

const SETTINGS_ROWS = [
  { key: 'businessDaysOnly', text: 'Send emails during business days only' },
  { key: 'randomized', text: 'Send emails randomly between 9AM - 6PM' },
  { key: 'failureEmail', text: 'Notify recipients who fail with a failure email' },
  {
    key: 'sendSummaryToRecipientList',
    text: 'Send the campaign summary to the recipient list on campaign end',
  },
]

const VECTORS = {
  link: { key: 'link', label: 'Link', format: null, counts: 'url' },
  attachmentHtml: {
    key: 'attachmentHtml',
    label: 'Attachment',
    format: 'HTML',
    counts: 'attachment',
  },
  attachmentPdf: {
    key: 'attachmentPdf',
    label: 'Attachment',
    format: 'PDF',
    counts: 'attachment',
  },
  attachmentDocx: {
    key: 'attachmentDocx',
    label: 'Attachment',
    format: 'DOCX',
    counts: 'attachment',
  },
  attachmentXls: {
    key: 'attachmentXls',
    label: 'Attachment',
    format: 'XLS',
    counts: 'xls',
  },
  dataEntry: { key: 'dataEntry', label: 'Data entry', format: null, counts: 'credentials' },
}

const VECTOR_BY_API = {
  link: 'link',
  url: 'link',
  dataEntry: 'dataEntry',
  data_entry: 'dataEntry',
  attachment: 'attachmentHtml',
}

const EMPTY_LANDING = { title: null, createdBy: null }

export const EMPTY_SIMULATION_CAMPAIGN_INFO = {
  available: false,
  campaign: { id: null, name: null, collectingUntil: null, collectingUntilIso: null },
  vector: null,
  phase: null,
  daysLeft: null,
  sendWindow: null,
  endedOn: null,
  delivered: 0,
  scheduled: 0,
  details: {
    fields: [],
    settings: [],
    templates: [],
    landingPage: EMPTY_LANDING,
    modules: [],
  },
}

const toText = (value) => {
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }
  return null
}

const attachmentVectorKey = (format) => {
  if (!format) {
    return 'attachmentHtml'
  }

  const normalized = String(format).toUpperCase()
  const byFormat = {
    HTML: 'attachmentHtml',
    PDF: 'attachmentPdf',
    DOCX: 'attachmentDocx',
    XLS: 'attachmentXls',
    XLSX: 'attachmentXls',
  }

  return byFormat[normalized] || 'attachmentHtml'
}

const resolveVector = (payload) => {
  if (payload.fileFormat || payload.vector === 'attachment') {
    return VECTORS[attachmentVectorKey(payload.fileFormat)]
  }

  const key = VECTOR_BY_API[payload.vector] || 'dataEntry'
  return VECTORS[key]
}

const resolvePhase = (payload) => {
  if (
    payload.phase === 'sending' ||
    payload.phase === 'collecting' ||
    payload.phase === 'completed'
  ) {
    return payload.phase
  }

  return payload.state === 'completed' ? 'completed' : 'collecting'
}

const formatReminders = (reminders) => {
  const asText = toText(reminders)
  if (asText) {
    return asText
  }
  if (!reminders || typeof reminders !== 'object' || reminders.everyXDays == null) {
    return null
  }

  const cadence = `Every ${reminders.everyXDays} day${reminders.everyXDays === 1 ? '' : 's'}`
  return reminders.total == null ? cadence : `${cadence}, ${reminders.total} total`
}

const computeDaysLeft = (collectingUntil) => {
  const deadline = parseCalendarDate(toCalendarIso(collectingUntil))
  if (!deadline) {
    return null
  }

  return differenceInCalendarDays(deadline, startOfDay(new Date()))
}

const computeSentDays = (window) => {
  if (window.sentDays != null) {
    return window.sentDays
  }

  const start = parseCalendarDate(window.start)
  if (!start || window.days == null) {
    return null
  }

  const elapsed = differenceInCalendarDays(startOfDay(new Date()), start) + 1
  if (elapsed < 1) {
    return 1
  }

  return Math.min(elapsed, window.days)
}

const field = (icon, label, value) => (value ? { icon, label, value } : null)

export const adaptSimulationCampaignInfo = (payload) => {
  if (payload == null || payload.available === false) {
    return EMPTY_SIMULATION_CAMPAIGN_INFO
  }

  const phase = resolvePhase(payload)
  const vector = resolveVector(payload)
  const emailsPerDay = payload.emailsPerDay ?? payload.sendWindow?.perDay
  const sendWindow = payload.sendWindow
    ? {
        days: payload.sendWindow.days,
        sentDays: computeSentDays(payload.sendWindow),
        perDay: emailsPerDay,
        start: payload.sendWindow.start,
        end: payload.sendWindow.end,
      }
    : null

  return {
    available: true,
    campaign: {
      id: payload.id ?? null,
      name: payload.name || null,
      vector: vector.key,
      collectingUntil: formatCampaignInfoDate(payload.collectingUntil),
      collectingUntilIso: toCalendarIso(payload.collectingUntil),
    },
    vector,
    phase,
    daysLeft: payload.daysLeft ?? computeDaysLeft(payload.collectingUntil),
    sendWindow,
    endedOn:
      toText(payload.endedOn) ||
      (phase === 'completed' ? formatCampaignInfoDate(payload.collectingUntil) : null),
    delivered: payload.delivered ?? 0,
    scheduled: payload.scheduled ?? 0,
    details: {
      fields: [
        field('Calendar', 'Delivery date', formatCampaignInfoDate(payload.deliveryDate)),
        field('Clock', 'Delivery time', toText(payload.deliveryTime)),
        field(
          'Calendar',
          'End date',
          formatCampaignInfoDate(payload.endDate || payload.sendWindow?.end)
        ),
        field('Send2', 'Emails/day', toText(emailsPerDay)),
        field('Flag', 'Collecting results until', formatCampaignInfoDate(payload.collectingUntil)),
        field('Global', 'Timezone', toText(payload.timezone)),
        field('Profile', 'Launched by', toText(payload.launchedBy)),
        field('Notification', 'Reminders', formatReminders(payload.reminders)),
      ].filter(Boolean),
      settings: SETTINGS_ROWS.map((row) => ({
        on: Boolean(payload.settings?.[row.key]),
        text: row.text,
      })),
      templates: payload.templates || [],
      landingPage: payload.landingPage || EMPTY_LANDING,
      modules: payload.modules || [],
    },
  }
}
